import type { AxiosError } from "axios";

import { capturePosthogException } from "config/posthog-runtime";
import { isMaintenanceApiResponse } from "utils/isMaintenanceApiResponse";

/** Expected client / auth failures — do not treat as Error Tracking noise. */
const SKIP_HTTP_STATUSES = new Set([400, 401, 403, 404, 409, 422]);

type ApiErrorBody = {
  code?: string;
  status?: string;
};

export type AxiosErrorCaptureContext = {
  requestPath: string | null;
};

export function shouldCaptureAxiosError(error: AxiosError): boolean {
  const status = error.response?.status;
  if (status == null) {
    return true;
  }

  const responseData = error.response?.data as ApiErrorBody | undefined;
  if (isMaintenanceApiResponse(status, responseData)) {
    return false;
  }

  if (SKIP_HTTP_STATUSES.has(status)) {
    return false;
  }

  if (status >= 500 || status === 429) {
    return true;
  }

  return false;
}

/**
 * Report unexpected Axios failures to PostHog Error Tracking.
 * Safe properties only: method, status, path, stable API code — no request/response bodies.
 */
export function captureAxiosErrorForPosthog(
  error: AxiosError,
  context: AxiosErrorCaptureContext
): void {
  if (!shouldCaptureAxiosError(error)) {
    return;
  }

  const status = error.response?.status;
  const method = (error.config?.method ?? "get").toUpperCase();
  const path = context.requestPath ?? "unknown";
  const responseData = error.response?.data as ApiErrorBody | undefined;
  const apiErrorCode = typeof responseData?.code === "string" ? responseData.code : null;

  capturePosthogException(error, {
    source: "axios",
    http_method: method,
    http_status: status ?? null,
    request_path: path,
    axios_code: error.code ?? null,
    api_error_code: apiErrorCode,
  });
}
